<template>
  <div>
    <!-- Clear Filter Button -->
    <!-- <div class="d-flex mb-2">
      <v-btn 
        v-if="hasActiveFilter" 
        @click="handleClearFilter()" 
        text 
        class="text-capitalize rounded-0" 
        color="primary" 
        dark
        small
      >
        <v-icon left dark small>mdi-filter-remove</v-icon>
        Clear Filter
      </v-btn>
    </div> -->

    <lb-datatableaction
      ref="dataTable"
      :headers="headers"
      :tabledata="tableData"
      :itemsperpage="itemsPerPage"
      :searchterm="searchTerm"
      :enableSaveOption="false"
      :bulkActions="false"
      :selectfilter="filterValues"
      :page_details="{}"
      :showClearFilter="showInternalClearFilter"
      :tableHeight="tableHeight"
      thead-class="summary-thead grayBackground"
      header-row-class="summary-header-row grayBackground"
      header-cell-class="summary-header-cell grayBackground ellipsis-on"
      class="summary-table"
      @filter-item="handleFilterChange"
      @clear-item="handleClearFilter"
    >
      <template v-slot:tbody>
        <tr
          v-for="(item, index) in filteredData"
          :key="index"
        >
          <td
            v-for="header in visibleHeaders"
            :key="header.value"
            :id="'automation-test-' + header.value"
            class="px-3 py-2"
            :style="{ 'height': '35px' }"
          >
            <span v-if="typeof item[header.value] === 'number'">
              {{ formatNumber(item[header.value]) }}
            </span>
            <span v-else>
              {{ isValidDate(item[header.value]) }}
            </span>
          </td>
        </tr>
      </template>
    </lb-datatableaction>
  </div>
</template>

<script>
export default {
  name: 'OnefileSummary',
  props: {
    tableData: {
      type: Array,
      required: true,
      default: () => []
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    searchTerm: {
      type: String,
      default: ''
    },
     showInternalClearFilter: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      filterValues: {},
      isClearing: false
    };
  },
  computed: {
    headers() {
      if (!this.tableData || this.tableData.length === 0) return [];
      
      const firstRow = this.tableData[0];
      return Object.keys(firstRow)
        .filter(key => key !== '_id' && key !== 'index') // Filter out _id and index
        .map(key => ({
          text: (key || "").replaceAll("_", " "),
          value: key,
          visible: true,
          sortable: false,
          filterable: true // Enable filtering for all columns
        }));
    },
    visibleHeaders() {
      return this.headers.filter(h => h.visible);
    },
    filteredData() {
      if (!this.tableData || this.tableData.length === 0) return [];
      
      // If no filters are applied, return all data
      if (!this.filterValues || Object.keys(this.filterValues).length === 0) {
        return this.tableData;
      }
      
      // Apply filters
      return this.tableData.filter(item => {
        return Object.entries(this.filterValues).every(([key, values]) => {
          if (!values || values.length === 0) return true;
          
          const itemValue = item[key];
          
          // Handle null/undefined values
          if (itemValue === null || itemValue === undefined) {
            return values.includes('NONE_SELECTED') || values.includes(null) || values.includes(undefined);
          }
          
          // Convert to string for comparison
          const itemValueStr = String(itemValue);
          return values.some(filterVal => String(filterVal) === itemValueStr);
        });
      });
    },
    hasActiveFilter() {
      return Object.values(this.filterValues || {}).some(v => Array.isArray(v) && v.length > 0);
    },
    tableHeight() {
      const rowCount = this.filteredData.length;
      const headerHeight = 48;
      const rowHeight = 35; // matching the row height in template
      const minHeight = 200;
      const maxHeight = 700;
      
      const calculatedHeight = headerHeight + (rowCount * rowHeight);
      return Math.min(Math.max(calculatedHeight, minHeight), maxHeight) + 'px';
    }
  },
  beforeDestroy() {
    // Clear filters when component is destroyed
    this.filterValues = {};
    
    // Call the child component's clearfilter method
    if (this.$refs.dataTable && typeof this.$refs.dataTable.clearfilter === 'function') {
      this.$refs.dataTable.clearfilter();
    }
  },
  methods: {
    handleFilterChange(filterObj) {
      this.filterValues = filterObj || {};
    },
    handleClearFilter() {
      // Prevent recursive calls
      if (this.isClearing) return;
      
      this.isClearing = true;
      
      // Clear filter locally without API call
      this.filterValues = {};
      
      // Call the child component's clearfilter method
      this.$nextTick(() => {
        if (this.$refs.dataTable && typeof this.$refs.dataTable.clearfilter === 'function') {
          this.$refs.dataTable.clearfilter();
        }
        
        // Reset flag after a short delay
        setTimeout(() => {
          this.isClearing = false;
        }, 100);
      });
    },
    formatNumber(value) {
      if (typeof value === 'number') {
        return this.$nova.formatNumber(value.toFixed(0));
      }
      return value;
    },
    isValidDate(value) {
      // You can implement date validation logic here
      // For now, just return the value as is
      return value;
    }
  }
};
</script>

<style scoped>
.summary-table ::v-deep .summary-thead {
  background-color: #f5f5f5 !important;
}

.summary-table ::v-deep .summary-header-row {
  background-color: #f5f5f5 !important;
}

.summary-table ::v-deep .summary-header-cell {
  font-weight: 600 !important;
  text-transform: capitalize !important;
  background-color: #f5f5f5 !important;
}

.summary-table ::v-deep .grayBackground {
  background-color: #f5f5f5 !important;
}

.summary-table ::v-deep thead th {
  background-color: #f5f5f5 !important;
}

.summary-table ::v-deep tbody tr:hover {
  background-color: #fafafa;
}

.summary-table ::v-deep tbody td {
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;
}

.summary-table ::v-deep .ellipsis-on {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
</style>
